import React, { useState, useEffect } from 'react';
import { Youtube, X, ExternalLink, CheckCircle, AlertCircle } from 'lucide-react';
import './YouTubeConnect.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

export const YouTubeConnect = ({ session, onConnectionChange }) => {
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState({ connected: false, channel: null, thumbnail: null });
    const [error, setError] = useState('');

    useEffect(() => {
        if (session?.access_token) {
            checkStatus();

            // Check for OAuth callback params in URL
            const params = new URLSearchParams(window.location.search);
            const code = params.get('youtube_code');
            const authError = params.get('youtube_error');

            if (code) {
                handleCallback(code);
                // Clean URL
                window.history.replaceState({}, document.title, window.location.pathname);
            } else if (authError) {
                setError(`YouTube Connection Failed: ${authError}`);
                window.history.replaceState({}, document.title, window.location.pathname);
            }
        }
    }, [session]);

    const checkStatus = async () => {
        try {
            const res = await fetch(`${API_BASE}/api/youtube/status`, {
                headers: { Authorization: `Bearer ${session.access_token}` }
            });
            const data = await res.json();
            if (res.ok) {
                setStatus({
                    connected: data.connected,
                    channel: data.channel,
                    thumbnail: data.thumbnail
                });
            }
        } catch (err) {
            console.error("Failed to check YouTube status", err);
        }
    };

    const handleConnect = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${API_BASE}/api/youtube/auth-url`, {
                headers: { Authorization: `Bearer ${session.access_token}` }
            });
            const data = await res.json();

            if (!res.ok) throw new Error(data.error || 'Failed to get auth URL');

            // Redirect to Google
            window.location.href = data.url;
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    const handleCallback = async (code) => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/api/youtube/connect`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${session.access_token}`
                },
                body: JSON.stringify({ code })
            });
            const data = await res.json();

            if (!res.ok) throw new Error(data.error || 'Connection failed');

            setStatus({ connected: true, channel: data.channel });
            if (onConnectionChange) onConnectionChange(true);

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDisconnect = async () => {
        if (!window.confirm("Are you sure you want to disconnect your YouTube account? Scheduled uploads may fail.")) return;

        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/api/youtube/disconnect`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${session.access_token}` }
            });

            if (res.ok) {
                setStatus({ connected: false, channel: null, thumbnail: null });
                if (onConnectionChange) onConnectionChange(false);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="youtube-connect-card">
            {error && (
                <div className="error-message">
                    <AlertCircle size={16} />
                    <span>{error}</span>
                    <button onClick={() => setError('')}><X size={14} /></button>
                </div>
            )}

            <div className="connect-content">
                {loading ? (
                    <div className="youtube-loading-spinner"></div>
                ) : status.connected ? (
                    <div className="connected-state">
                        {status.thumbnail ? (
                            <img
                                src={status.thumbnail}
                                alt={status.channel}
                                className="channel-avatar"
                            />
                        ) : (
                            <div className="avatar-placeholder">
                                <CheckCircle size={32} color="#10b981" />
                            </div>
                        )}
                        <span className="channel-name">{status.channel}</span>
                        <button className="disconnect-btn" onClick={handleDisconnect}>
                            Disconnect
                        </button>
                    </div>
                ) : (
                    <div className="disconnected-state">
                        <div className="youtube-icon-large">
                            <Youtube size={48} color="#FF0000" />
                        </div>
                        <button className="connect-btn" onClick={handleConnect}>
                            Connect YouTube Channel
                            <ExternalLink size={16} />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );

};
